import { useId } from 'react';
import { T, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { useTaskTypes } from 'src/api/rpc/hooks/tasks/useTaskTypes';
import { PageIconPicker } from 'src/components/task-pages/PageIconPicker';
import { EditorDialogShell } from '../../EditorDialogShell';
import { PagePlacementAudienceFields } from '../shared/PagePlacementAudienceFields';
import { TaskPageColumnCustomizationToggle } from './TaskPageDefaultColumnsField';
import { TaskPageTaskTypesField } from './TaskPageTaskTypesField';
import {
  isTaskPageDraftDirty,
  isTaskPageDraftValid,
  type TaskPageDraft,
} from './taskPageAdmin';

/** The editor's form body — only mounted while the dialog is open. */
function TaskPageEditorFields({
  draft,
  onChange,
}: {
  draft: TaskPageDraft;
  onChange: (next: TaskPageDraft) => void;
}) {
  const gt = useGT();
  const idPrefix = useId();
  const { data: taskTypes } = useTaskTypes({ enabled: true });

  return (
    <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto px-6 py-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-name`}>
          <T context="task page form field label">Name</T>
        </Label>
        <Input
          id={`${idPrefix}-name`}
          value={draft.name}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
          placeholder={gt('Invoice Emails', {
            context: 'example task page name placeholder',
          })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-description`}>
          <T context="task page form field label">Description</T>
        </Label>
        <Textarea
          id={`${idPrefix}-description`}
          value={draft.description}
          rows={2}
          onChange={(e) => onChange({ ...draft, description: e.target.value })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>
          <T context="task page form field label">Icon</T>
        </Label>
        <PageIconPicker
          value={draft.icon}
          onChange={(icon) => onChange({ ...draft, icon })}
        />
      </div>
      <TaskPageTaskTypesField
        id={`${idPrefix}-task-types`}
        value={draft.taskTypes}
        onChange={(next) => onChange({ ...draft, taskTypes: next })}
        taskTypes={taskTypes}
      />
      <PagePlacementAudienceFields
        idPrefix={idPrefix}
        draft={draft}
        onChange={onChange}
        audienceLabel={<T context="task page form field label">Audience</T>}
        audiencePlaceholder={gt('Everyone with page access', {
          context:
            'audience picker placeholder in the task page editor, shown when no user groups are selected',
        })}
        audienceHint={
          <T context="hint under the task page audience field">
            Limit the page to these user groups. Leave empty to show it to
            everyone who can view task pages.
          </T>
        }
      />
      <TaskPageColumnCustomizationToggle
        id={`${idPrefix}-column-customization`}
        checked={draft.allowUserColumnCustomization}
        onChange={(allowUserColumnCustomization) =>
          onChange({ ...draft, allowUserColumnCustomization })
        }
      />
    </div>
  );
}

/**
 * Create/edit dialog for one task page: identity, sidebar placement, the
 * task types it shows (one tab each), and the user-group audience (empty = every TaskPage
 * reader). Mirrors the task type editor dialog patterns.
 */
export function TaskPageEditorDialog({
  draft,
  initialDraft,
  onChange,
  onClose,
  onSave,
  isSaving,
}: {
  draft: TaskPageDraft | null;
  /** The draft as opened, for the unsaved-changes warning. */
  initialDraft: TaskPageDraft | null;
  onChange: (next: TaskPageDraft) => void;
  onClose: () => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  const gt = useGT();
  const dirty =
    draft !== null &&
    initialDraft !== null &&
    isTaskPageDraftDirty(draft, initialDraft);
  const canSave = draft !== null && isTaskPageDraftValid(draft);
  const isEditing = draft?.id != null;

  return (
    <EditorDialogShell
      open={draft !== null}
      dirty={dirty}
      onClose={onClose}
      title={
        isEditing ? (
          <T context="dialog title, edit a task page">Edit task page</T>
        ) : (
          <T context="dialog title, create a task page">New task page</T>
        )
      }
      discard={{
        title: gt('Discard unsaved changes?', {
          context: 'unsaved-changes leave warning dialog title',
        }),
        message: gt(
          'Discard unsaved changes to this task page? Your edits will be lost.',
          {
            context:
              'confirm dialog before closing the task page editor with unsaved changes',
          },
        ),
        confirmText: gt('Leave without saving', {
          context: 'unsaved-changes leave warning, confirm button',
        }),
        cancelText: gt('Keep editing', {
          context: 'unsaved-changes leave warning, cancel button',
        }),
      }}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose} disabled={isSaving}>
            <T context="button, close the task page editor">Cancel</T>
          </Button>
          <Button disabled={!canSave || isSaving} onClick={onSave}>
            {isSaving ? (
              <T context="button label while saving a task page">Saving…</T>
            ) : isEditing ? (
              <T context="button, save the task page changes">Save</T>
            ) : (
              <T context="button, create the task page">Create</T>
            )}
          </Button>
        </>
      )}
    >
      {draft && <TaskPageEditorFields draft={draft} onChange={onChange} />}
    </EditorDialogShell>
  );
}
